import React, { useRef, useState } from "react";
import {
	Image as KonvaImage,
	Transformer,
	Group,
	Text as KonvaText,
	TextPath as KonvaTextPath,
} from "react-konva";
import {
	computeStickerTextLayout,
	useStickerFontsReady,
} from "./CustomStickers/fitText";

/**
 * Sticker - Componente "tonto" que recibe posición y tamaño del padre.
 * 
 * Toda la lógica de anclaje y escalado vive en StickerApp.
 * Este componente solo:
 * - Recibe displayX, displayY, displayWidth, displayHeight del padre
 * - Renderiza la imagen en esa posición y tamaño
 * - Al soltar/transformar, envía las coordenadas Stage al padre
 */
const Sticker = React.memo(
	({
		stickerProps,
		displayX,
		displayY,
		displayWidth,
		displayHeight,
		isSelected,
		onSelect,
		onChange,
		onDelete,
		tabbar,
		key,
		handleContextMenu,
		onTransformStart,
		onTransformEnd,
		getCanvasWidth,
		getCanvasHeight,
	}) => {
		const imageRef = useRef();
		const trRef = useRef();
		
		// Estado para forzar re-render cuando la imagen carga
		const [imageLoaded, setImageLoaded] = useState(false);
		
		// Refs para detectar scroll durante touch
		const touchStartRef = useRef(null);
		const hasMovedRef = useRef(false);

		// Efecto para manejar la carga de la imagen
		React.useEffect(() => {
			const img = stickerProps.image;
			console.log("[Sticker useEffect] image:", img?.src, "complete:", img?.complete, "naturalWidth:", img?.naturalWidth);
			if (!img) return;
			
			// Si ya está cargada, marcar como loaded
			if (img.complete && img.naturalWidth > 0) {
				console.log("[Sticker useEffect] Imagen ya cargada, setImageLoaded(true)");
				setImageLoaded(true);
				return;
			}
			
			// Si no, escuchar el evento load
			const handleLoad = () => {
				console.log("[Sticker useEffect] Evento load disparado, setImageLoaded(true)");
				setImageLoaded(true);
			};
			
			console.log("[Sticker useEffect] Añadiendo listener load");
			img.addEventListener('load', handleLoad);
			return () => {
				img.removeEventListener('load', handleLoad);
			};
		}, [stickerProps.image]);

		// Efecto para configurar el Transformer cuando está seleccionado
		React.useEffect(() => {
			if (isSelected && trRef.current && imageRef.current) {
				trRef.current.nodes([imageRef.current]);
				trRef.current.getLayer().batchDraw();
			}
		}, [isSelected]);

		// Fuerza re-render cuando la fuente del sticker personalizado está lista,
		// para que Konva la use en lugar del fallback. Se llama ANTES del early
		// return para respetar las rules of hooks.
		// eslint-disable-next-line no-unused-vars
		const stickerFontsReady = useStickerFontsReady(
			stickerProps.customText?.config?.fontFamily
				? [stickerProps.customText.config.fontFamily]
				: [],
		);

		// Verificar que la imagen esté cargada correctamente antes de renderizar
		const imgReady = stickerProps.image && 
			stickerProps.image.complete && 
			stickerProps.image.naturalWidth > 0 && 
			stickerProps.image.naturalHeight > 0;
		
		console.log("[Sticker render] imageLoaded:", imageLoaded, "imgReady:", imgReady, "image:", stickerProps.image?.src?.slice(-30));
		
		if (!imgReady) {
			console.log("[Sticker render] Devolviendo null porque imagen no está lista");
			return null;
		}

		// Posición y tamaño: usar lo que el padre pasa (calculado en StickerApp)
		const pixelX = displayX ?? 0;
		const pixelY = displayY ?? 0;
		const renderWidth = displayWidth ?? stickerProps.width ?? 80;
		const renderHeight = displayHeight ?? stickerProps.height ?? 80;

		// Filtrar props que colisionan con Konva nativo
		// (offsetX, offsetY en Konva desplazan el origen del nodo)
		const {
			offsetX: _anchorOffsetX,
			offsetY: _anchorOffsetY,
			offsetInPixels: _offsetInPixels,
			anchor: _anchor,
			anchorType: _anchorType,
			anchorBlockId: _anchorBlockId,
			blockOffsetXPercent: _blockOffsetXPercent,
			blockOffsetYPercent: _blockOffsetYPercent,
			xPercent: _xPercent,
			yPercent: _yPercent,
			widthPercent: _widthPercent,
			heightPercent: _heightPercent,
			originalCanvasWidth: _originalCanvasWidth,
			originalWidth: _originalWidth,
			originalHeight: _originalHeight,
			...konvaProps
		} = stickerProps;

		// Handler para detectar si hubo scroll antes de ejecutar el tap
		const handleSelect = (e) => {
			if (hasMovedRef.current) {
				hasMovedRef.current = false;
				touchStartRef.current = null;
				return;
			}
			onSelect(e);
		};

		// Handler para touchstart - rastrear posición inicial
		const handleTouchStart = (e) => {
			const touch = e.evt?.touches?.[0] || e.evt?.changedTouches?.[0];
			if (touch) {
				touchStartRef.current = {
					x: touch.clientX,
					y: touch.clientY,
					time: Date.now()
				};
				hasMovedRef.current = false;
			}
		};

		// Handler para touchmove - detectar si hay movimiento
		const handleTouchMove = (e) => {
			if (touchStartRef.current) {
				const touch = e.evt?.touches?.[0] || e.evt?.changedTouches?.[0];
				if (touch) {
					const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
					const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
					if (deltaX > 5 || deltaY > 5) {
						hasMovedRef.current = true;
					}
				}
			}
		};

		// Handler para touchend - resetear estado
		const handleTouchEnd = () => {
			setTimeout(() => {
				hasMovedRef.current = false;
				touchStartRef.current = null;
			}, 100);
		};

		const customText = stickerProps.customText;

		const sharedHandlers = {
			onContextMenu: handleContextMenu,
			onClick: handleSelect,
			onTap: handleSelect,
			onTouchStart: handleTouchStart,
			onTouchMove: handleTouchMove,
			onTouchEnd: handleTouchEnd,
			draggable: true,
			onDragStart: () => {
				if (onTransformStart) onTransformStart();
			},
			onDragEnd: (e) => {
				if (onTransformEnd) onTransformEnd();

				const dropX = e.target.x();
				const dropY = e.target.y();
				const canvasWidth = getCanvasWidth ? getCanvasWidth() : 800;
				const canvasHeight = getCanvasHeight ? getCanvasHeight() : 1000;

				if (
					dropX < -renderWidth ||
					dropX > canvasWidth ||
					dropY < -renderHeight ||
					dropY > canvasHeight
				) {
					onDelete();
				} else {
					onChange({
						...stickerProps,
						x: dropX,
						y: dropY,
					});
				}
			},
			onTransformEnd: (_e) => {
				const node = imageRef.current;
				const scaleX = node.scaleX();
				const scaleY = node.scaleY();

				node.scaleX(1);
				node.scaleY(1);

				const newWidth = Math.max(5, node.width() * scaleX);
				const newHeight = Math.max(5, node.height() * scaleY);

				onChange({
					...stickerProps,
					x: node.x(),
					y: node.y(),
					width: newWidth,
					height: newHeight,
					rotation: node.rotation(),
				});

				if (onTransformEnd) {
					setTimeout(() => onTransformEnd(), 0);
				}
			},
		};

		const textLayout = customText
			? computeStickerTextLayout(
					customText.value,
					customText.config,
					renderWidth,
					renderHeight,
				)
			: null;

		return (
			<React.Fragment key={key}>
				{customText ? (
					<Group
						ref={imageRef}
						{...konvaProps}
						x={pixelX}
						y={pixelY}
						width={renderWidth}
						height={renderHeight}
						{...sharedHandlers}
					>
						{/* La imagen debe escuchar eventos para que el Group
						     reciba clicks/drags. Si ponemos listening=false en
						     todos los hijos, el Group no tiene hit area y no
						     se puede arrastrar. */}
						<KonvaImage
							image={stickerProps.image}
							x={0}
							y={0}
							width={renderWidth}
							height={renderHeight}
						/>
						{textLayout && textLayout.curve ? (
							<KonvaTextPath
								x={0}
								y={0}
								data={textLayout.curve.pathAbs}
								text={textLayout.text}
								fontFamily={textLayout.fontFamily}
								fontStyle={textLayout.konvaFontStyle}
								fontSize={textLayout.fontSize}
								fill={textLayout.color}
								stroke={textLayout.stroke || undefined}
								strokeWidth={textLayout.strokeWidth || 0}
								fillAfterStrokeEnabled
								letterSpacing={textLayout.letterSpacing}
								align={textLayout.align}
								shadowColor={textLayout.shadow?.color}
								shadowOffsetX={textLayout.shadow?.offsetX}
								shadowOffsetY={textLayout.shadow?.offsetY}
								shadowBlur={textLayout.shadow?.blur}
								shadowOpacity={textLayout.shadow ? 1 : 0}
								listening={false}
							/>
						) : textLayout && textLayout.verticalRotate ? (
							<Group
								x={
									textLayout.rect.x +
									textLayout.rect.width / 2
								}
								y={
									textLayout.rect.y +
									textLayout.rect.height / 2
								}
								rotation={90}
							>
								<KonvaText
									x={0}
									y={0}
									text={textLayout.text}
									fontFamily={textLayout.fontFamily}
									fontStyle={textLayout.konvaFontStyle}
									fontSize={textLayout.fontSize}
									fill={textLayout.color}
									stroke={textLayout.stroke || undefined}
									strokeWidth={textLayout.strokeWidth || 0}
									fillAfterStrokeEnabled
									scaleX={textLayout.scaleX ?? 1}
									letterSpacing={textLayout.letterSpacing}
									align="center"
									verticalAlign="middle"
									shadowColor={textLayout.shadow?.color}
									shadowOffsetX={textLayout.shadow?.offsetX}
									shadowOffsetY={textLayout.shadow?.offsetY}
									shadowBlur={textLayout.shadow?.blur}
									shadowOpacity={textLayout.shadow ? 1 : 0}
									listening={false}
								/>
							</Group>
						) : textLayout && textLayout.vertical ? (
							<KonvaText
								x={textLayout.rect.x}
								y={textLayout.rect.y}
								width={textLayout.rect.width}
								height={textLayout.rect.height}
								text={Array.from(textLayout.text).join("\n")}
								fontFamily={textLayout.fontFamily}
								fontStyle={textLayout.konvaFontStyle}
								fontSize={textLayout.fontSize}
								fill={textLayout.color}
								stroke={textLayout.stroke || undefined}
								strokeWidth={textLayout.strokeWidth || 0}
								fillAfterStrokeEnabled
								lineHeight={textLayout.verticalLineHeight ?? 1.12}
								letterSpacing={textLayout.letterSpacing}
								align="center"
								verticalAlign="middle"
								shadowColor={textLayout.shadow?.color}
								shadowOffsetX={textLayout.shadow?.offsetX}
								shadowOffsetY={textLayout.shadow?.offsetY}
								shadowBlur={textLayout.shadow?.blur}
								shadowOpacity={textLayout.shadow ? 1 : 0}
								listening={false}
							/>
						) : (
							textLayout &&
							(() => {
								// Para nombres largos aplicamos una compresión horizontal
								// (scaleX < 1) manteniendo el texto centrado en su rect.
								const sX = textLayout.scaleX ?? 1;
								const shiftX =
									textLayout.align === "left"
										? 0
										: textLayout.align === "right"
											? textLayout.rect.width * (1 - sX)
											: (textLayout.rect.width * (1 - sX)) / 2;
								return (
									<KonvaText
										x={textLayout.rect.x + shiftX}
										y={
											textLayout.rect.y +
											(textLayout.rect.height -
												textLayout.fontSize * 1.15) /
												2
										}
										width={textLayout.rect.width}
										scaleX={sX}
										text={textLayout.text}
										fontFamily={textLayout.fontFamily}
										fontStyle={textLayout.konvaFontStyle}
										fontSize={textLayout.fontSize}
										fill={textLayout.color}
										stroke={textLayout.stroke || undefined}
										strokeWidth={textLayout.strokeWidth || 0}
										fillAfterStrokeEnabled
										letterSpacing={textLayout.letterSpacing}
										align={textLayout.align}
										lineHeight={1.15}
										shadowColor={textLayout.shadow?.color}
										shadowOffsetX={textLayout.shadow?.offsetX}
										shadowOffsetY={textLayout.shadow?.offsetY}
										shadowBlur={textLayout.shadow?.blur}
										shadowOpacity={textLayout.shadow ? 1 : 0}
										listening={false}
									/>
								);
							})()
						)}
					</Group>
				) : (
					<KonvaImage
						ref={imageRef}
						{...konvaProps}
						x={pixelX}
						y={pixelY}
						width={renderWidth}
						height={renderHeight}
						{...sharedHandlers}
					/>
				)}
				{isSelected && (
					<Transformer
						ref={trRef}
						flipEnabled={true}
						boundBoxFunc={(oldBox, newBox) => {
							if (Math.abs(newBox.width) < 5 || Math.abs(newBox.height) < 5) {
								return oldBox;
							}
							return newBox;
						}}
						anchorSize={12}
						anchorCornerRadius={2}
						anchorFill="#6366F1"
						anchorStroke="#8B5CF6"
						borderStroke="#8B5CF6"
						onTransformStart={() => {
							if (onTransformStart) onTransformStart();
						}}
					/>
				)}
			</React.Fragment>
		);
	}
);

export default Sticker;
